import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import authSlice from "../../store/slices/auth.slice";
import { Link, useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { useTranslation } from "react-i18next";
import { Badge, Dropdown, ListGroup, Spinner } from "react-bootstrap";
import { BellIcon, LanguageIcon, ThemeIcon } from "../components/Icons";
import { NotificationsService } from "../../app/notifications/service";
import { handleError } from "../functions/handleError";
import { usePagination } from "../hooks/usePagination";
import moment from "moment";
import { INotification } from "../interfaces/notification/INotification";
import { applyThemeMode, getStoredThemeMode, toggleThemeMode } from "../functions/theme";
import ImpersonationBanner from "../components/ImpersonationBanner";

const Header = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  const [notifications, setNotifications] = useState<
    INotification[] | undefined
  >(undefined);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const { pagination } = usePagination({
    perPage: 25,
  });

  const handleThemeToggle = () => {
    toggleThemeMode();
  };
  const handleToggleSidebar = () => {
    // data-toggled="icon-overlay-close"
    const current = document.documentElement.getAttribute("data-toggled");
    const next =
      current === "icon-overlay-close"
        ? "icon-overlay-open"
        : "icon-overlay-close";
    document.documentElement.setAttribute("data-toggled", next);
  };

  const handleLogout = () => {
    const themeMode = getStoredThemeMode();
    localStorage.clear();
    applyThemeMode(themeMode);
    navigate("/");
    dispatch(authSlice.actions.setLogoutState());
  };

  const handleBellClick = () => {
    NotificationsService.getAll(pagination)
      .then((response) => {
        setNotifications(response?.data || []);
        setUnreadCount(0);
      })
      .catch(handleError);
  };

  useEffect(() => {
    const fetchUnread = () => {
      NotificationsService.getUnread()
        .then((response) => {
          setUnreadCount(response?.unreadNotificationsCount || 0);
        })
        .catch(handleError);
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000); //refresh every minute
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <header className="app-header sticky" id="header">
        <div className="main-header-container container-fluid">
          <div className="header-content-left">
            <div className="header-element">
              <div className="horizontal-logo">
                <div
                  onClick={() => navigate("/dashboard")}
                  className="header-logo"
                >
                  <img
                    src="/assets/images/brand-logos/desktop-logo.png"
                    alt="logo"
                    className="desktop-logo"
                  />
                  <img
                    src="/assets/images/brand-logos/toggle-logo.svg"
                    alt="logo"
                    className="toggle-logo"
                  />
                  <img
                    src="/assets/images/brand-logos/desktop-logo.png"
                    alt="logo"
                    className="desktop-dark"
                  />
                  <img
                    src="/assets/images/brand-logos/toggle-dark.svg"
                    alt="logo"
                    className="toggle-dark"
                  />
                </div>
              </div>
            </div>
            <div className="header-element mx-lg-0 mx-2">
              <a
                aria-label="Hide Sidebar"
                className="sidemenu-toggle header-link animated-arrow hor-toggle horizontal-navtoggle"
                data-bs-toggle="sidebar"
                href="#"
                onClick={handleToggleSidebar}
              >
                <span />
              </a>
            </div>
            <ImpersonationBanner />
          </div>
          <ul className="header-content-right list-unstyled">
            <Dropdown
              className="header-element notifications-dropdown d-xl-block d-none"
              align="end"
              autoClose="outside"
              onClick={handleBellClick}
            >
              <Dropdown.Toggle
                id="messageDropdown"
                variant="transparent"
                className="header-link no-caret border-0"
              >
                <div className="position-relative">
                  <BellIcon isAnimated={unreadCount > 0} />
                  {unreadCount > 0 && (
                    <span
                      className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                      style={{ fontSize: "0.6rem" }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu
                className="p-0 dropdown-menu-end shadow-lg border-0"
                style={{ minWidth: 280, maxHeight: 400, overflowY: "auto" }}
              >
                <div className="p-3 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-semibold">
                    {t("notifications.notifications")}
                  </h6>
                  <Badge bg="secondary-subtle" text="dark">
                    {unreadCount} {t("notifications.unread")}
                  </Badge>
                </div>

                <hr className="my-0" />

                {notifications === undefined && (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t("general.loading")}
                  </div>
                )}

                {notifications !== undefined && notifications?.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    <i className="ri-notification-off-line fs-2 mb-2 d-block"></i>
                    {t("notifications.none")}
                  </div>
                )}

                {notifications !== undefined && (
                  <ListGroup variant="flush">
                    {notifications?.map((notification, index) => {
                      const isFlatNotification =
                        notification.notificationType?.startsWith("flat_") ||
                        notification.notificationType === "new_flat_added";
                      const isMaterialNotification =
                        notification.notificationType?.startsWith("material_");
                      const flat = notification?.flat;
                      const offerFlat = notification?.offer?.flat;
                      const offerId = notification?.offer?.id;

                      const handleNotificationClick = () => {
                        if (isFlatNotification && flat) {
                          const customerId = flat.location?.customer?.id;
                          const locationId = flat.location?.id;
                          const flatId = flat.id;

                          if (customerId && locationId && flatId) {
                            navigate(`/customers/${customerId}/locations/${locationId}/flats/${flatId}`);
                          }
                        } else if (isMaterialNotification && offerId) {
                          navigate(`/offers/material-list/${offerId}`);
                        } else if (offerId) {
                          navigate(`/offers/preview/${offerId}`);
                        }
                      };

                      return (
                        <ListGroup.Item
                          key={notification.id || index}
                          className="px-3"
                          onClick={handleNotificationClick}
                          action
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="me-2">
                              <div className="fw-semibold">
                                {isFlatNotification
                                  ? flat?.name || offerFlat?.name
                                  : notification?.offer?.name}
                              </div>
                              <div className="text-muted small text-truncate">
                                {t(
                                  `notifications.${notification.notificationType}_desc`,
                                )}
                              </div>
                              <small className="text-muted">
                                {moment(notification?.createdAt).fromNow()}
                              </small>
                            </div>
                            {!notification.isSeen && (
                              <span className="badge bg-danger-subtle text-danger rounded-pill">
                                •
                              </span>
                            )}
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}
                <div className="border-top text-center py-2 bg-light">
                  <Link
                    to={"/notifications"}
                    className="text-primary fw-semibold"
                  >
                    {t("notifications.viewAll")}{" "}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <div className="header-element country-selector dropdown">
              <a
                role="button"
                tabIndex={0}
                href="#!"
                id="react-aria5497447103-«ra»"
                aria-expanded="false"
                data-bs-auto-close="outside"
                data-bs-toggle="dropdown"
                className="header-link dropdown-toggle no-caret border-0 dropdown-toggle btn"
              >
                <LanguageIcon />
              </a>
              <div
                data-popper-placement="bottom-start"
                x-placement="bottom-start"
                aria-labelledby="react-aria5497447103-«ra»"
                className="main-header-dropdown dropdown-menu dropdown-menu-end dropdown-menu"
                data-popper-reference-hidden="false"
                data-popper-escaped="false"
                style={{
                  position: "absolute",
                  inset: "0px auto auto 0px",
                  transform: "translate(1345px, 53px)",
                }}
              >
                <li>
                  <div className="py-2 px-3">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search Here"
                      aria-label=".form-control-sm example"
                      type="text"
                    />
                  </div>
                </li>
                <li>
                  <a
                    className="dropdown-item d-flex align-items-center justify-content-between"
                    href="#!"
                    data-discover="true"
                    onClick={(e) => {
                      e.preventDefault();
                      const lang = "gb";
                      try { localStorage.setItem("i18nextLng", lang); } catch { }
                      try { localStorage.setItem("language", lang); } catch { }
                      try { document.documentElement.setAttribute("lang", "en"); } catch { }
                      i18n.changeLanguage(lang).catch(() => { });
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <span className="avatar avatar-rounded avatar-xs lh-1 me-2">
                        <img
                          alt="img"
                          src="data:image/jpeg;base64,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"
                        />
                      </span>
                      English
                    </div>
                    <span className="text-muted fs-12">(US)</span>
                  </a>
                </li>


                <li>
                  <a
                    className="dropdown-item d-flex align-items-center justify-content-between"
                    href="#!"
                    data-discover="true"
                    onClick={(e) => {
                      e.preventDefault();
                      const lang = "de";
                      try { localStorage.setItem("i18nextLng", lang); } catch { }
                      try { localStorage.setItem("language", lang); } catch { }
                      try { document.documentElement.setAttribute("lang", "de"); } catch { }
                      i18n.changeLanguage(lang).catch(() => { });
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <span className="avatar avatar-rounded avatar-xs lh-1 me-2">
                        <img
                          alt="img"
                          src="data:image/jpeg;base64,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"
                        />
                      </span>
                      Deutsch
                    </div>
                    <span className="text-muted fs-12">(US)</span>
                  </a>
                </li>
              </div>
            </div>
            <li className="header-element header-theme-mode">
              <a
                href="#"
                onClick={handleThemeToggle}
                className="header-link layout-setting"
              >
                <span className="light-layout">
                  <ThemeIcon />
                </span>
                <span className="dark-layout">
                  <ThemeIcon />
                </span>
              </a>
            </li>
            <div className="header-element dropdown dropdown">
              <a
                role="button"
                tabIndex={0}
                href="#!"
                id="mainHeaderProfile"
                aria-expanded="false"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                className="header-link dropdown-toggle no-caret dropdown-toggle btn"
              >
                <div className="d-flex align-items-center">
                  <div className="me-xl-2 me-0">
                    <img
                      alt="img"
                      className="avatar avatar-sm avatar-rounded object-fit-cover"
                      src={
                        userData?.profileImage?.path ??
                        "https://react.spruko.com/zynix-ts/preview/assets/2-b7cXhTxJ.jpg"
                      }
                    />
                  </div>
                  <div className="d-xl-block d-none lh-1">
                    <span className="fw-medium lh-1">
                      {userData?.firstName}
                    </span>
                  </div>
                </div>
              </a>
              <div
                aria-labelledby="mainHeaderProfile"
                className="main-header-dropdown dropdown-menu pt-0 overflow-hidden header-profile-dropdown dropdown-menu-end dropdown-menu"
                data-popper-reference-hidden="false"
                data-popper-escaped="false"
                data-popper-placement="bottom-end"
                style={{
                  position: "absolute",
                  inset: "0px 0px auto auto",
                  transform: "translate(0px, 36px)",
                }}
              >
                <li>
                  <div className="py-2 px-3">
                    <span className="fw-semibold">
                      {userData?.firstName} {userData?.lastName}
                    </span>
                    <span className="d-block text-muted">
                      {userData?.roles
                        ?.map((role: string) => t(`roles.${role}`))
                        .join(", ")}
                    </span>
                  </div>
                </li>
                <Link
                  to={"/profile"}
                  className="dropdown-item d-flex align-items-center"
                >
                  <i className="ti ti-user text-primary me-2 fs-16" />
                  {t("general.profile")}
                </Link>
                <li className="py-2 px-3">
                  <button
                    className="btn btn-primary btn-sm w-100"
                    onClick={handleLogout}
                  >
                    {t("auth.logout")}
                  </button>
                </li>
              </div>
            </div>
          </ul>
        </div>
      </header>
      <div id={"responsive-overlay"}></div>
    </>
  );
};

export default Header;
